import { useApolloClient } from "@apollo/client";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { ConfirmationDialog } from "../../../../componentLibrary";
import {
  StaffDashboardRecipePreviewFragment,
  useDeleteRecipeMutation,
  useEditRecipeIsSharedMutation,
  useStaffDashboardRecipesQuery,
} from "../../../../types";
import GeneralizedStaffContent from "../../../../views/Staff/GeneralizedStaffContent";
import { UseFetchQueryArgs } from "../../../../views/Staff/GeneralizedStaffContent/types";
import { getStaffRecipeNavigation } from "./FoodManagementRouter";

const headerRowInfo = [
  { key: "name", displayName: "Name" },
  { key: "createdByName", displayName: "Created By" },
  { key: "mealTypes", displayName: "Meal Types" },
  { key: "yields", displayName: "Yields" },
  { key: "createdAt", displayName: "Date Created" },
  { key: "updatedAt", displayName: "Last Updated" },
];

const useFetchQuery = (query: UseFetchQueryArgs) => {
  const { data, loading } = useStaffDashboardRecipesQuery(query);

  return {
    data: data?.recipeOffsetConnection,
    loading,
  };
};

const getCreatedByName = ({ createdBy }: StaffDashboardRecipePreviewFragment): string => {
  if (!createdBy) {
    return `--`;
  }
  if (createdBy.firstName || createdBy.lastName) {
    return `${createdBy.firstName} ${createdBy.lastName}`;
  }
  return createdBy.id;
};

export const RecipesPage = () => {
  const { setMessage } = useSnackbar();
  const { formatDateWithLocale } = useDateFormatting();

  const convertObjToRowData = (recipe: StaffDashboardRecipePreviewFragment) => ({
    name: recipe.name,
    createdByName: getCreatedByName(recipe),
    mealTypes:
      recipe.mealTypes
        ?.map(mt => {
          const mealTypeString = mt.toString();
          return mealTypeString.charAt(0).toUpperCase() + mealTypeString.slice(1);
        })
        .sort()
        .join(", ") ?? "",
    yields: recipe.servings.map(serving => `${serving.perRecipeYield} ${serving.units}`).join(", "),
    isShared: recipe.isShared,
    createdAt: formatDateWithLocale(new Date(recipe.createdAt)),
    updatedAt: formatDateWithLocale(new Date(recipe.updatedAt)),
  });
  const apolloClient = useApolloClient();
  const navigate = useNavigate();

  const resetTable = () => {
    apolloClient.cache.evict({
      fieldName: "recipeOffsetConnection",
      broadcast: true,
    });
  };

  const [deleteRecipe] = useDeleteRecipeMutation({
    onCompleted: () => {
      resetTable();
      setDeleteableRecipe(null);
      setMessage("success", `Successfully deleted recipe`);
    },
  });
  const handleDelete = (recipeId: string) => {
    deleteRecipe({
      variables: { input: { recipeId } },
    });
  };

  const [editRecipeIsShared] = useEditRecipeIsSharedMutation();
  const handleEditIsShared = (id: string, isShared: boolean) => {
    const message = isShared ? "Shared recipe with orgs" : "Unshared recipe";
    setMessage("success", message);
    apolloClient.cache.modify({
      id: `Recipe:${id}`,
      fields: {
        isShared: () => isShared,
      },
    });
    editRecipeIsShared({
      variables: { input: { id, isShared } },
    });
  };

  const onDuplicateRecipe = (recipe: StaffDashboardRecipePreviewFragment) => {
    const { to, options } = getStaffRecipeNavigation({ duplicatedRecipeId: recipe.id, isDuplicate: true });
    navigate(to, options);
  };

  const onDeleteRecipe = (recipe: StaffDashboardRecipePreviewFragment) => {
    setDeleteableRecipe(recipe);
  };

  const [deleteableRecipe, setDeleteableRecipe] = useState<StaffDashboardRecipePreviewFragment | null>(null);

  return (
    <>
      <GeneralizedStaffContent
        togglable={{
          headerRowInfo: { displayName: "Shared", key: "isShared" },
          getToggleValue: recipe => recipe.isShared,
          onToggle: recipe => handleEditIsShared(recipe.id, !recipe.isShared),
          disabled: recipe => recipe.isShared,
          tooltip: () => "Notemeal recipes cannot be unshared",
        }}
        onTableRowClick={recipe => {
          const { to, options } = getStaffRecipeNavigation({}, recipe.id);
          navigate(to, options);
        }}
        headerRowInfo={headerRowInfo}
        convertObjToRowData={convertObjToRowData}
        queryCacheKey="allTeamLabels"
        useFetchQuery={useFetchQuery}
        entityName="Recipe"
        getObjDisplay={recipe => recipe.name}
        onCreateSuccessMessage={recipe => `Succesfully created ${recipe.name}`}
        onEditSuccessMessage={recipe => `Succesfully edited ${recipe.name}`}
        actionMenuItems={[
          {
            label: "Duplicate",
            action: onDuplicateRecipe,
          },
          { label: "Delete", action: onDeleteRecipe },
        ]}
        onCreate={() => {
          const { to, options } = getStaffRecipeNavigation();
          navigate(to, options);
        }}
      />
      {deleteableRecipe !== null && (
        <ConfirmationDialog
          open={!!deleteableRecipe}
          title="Delete Recipe"
          message={`Are you sure you want to delete this recipe?${
            deleteableRecipe.isShared ? " It has already been shared with orgs." : ""
          }`}
          onCancel={() => setDeleteableRecipe(null)}
          onConfirm={() => handleDelete(deleteableRecipe.id)}
          variant="containedDestructive"
        />
      )}
    </>
  );
};
